import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  TextField,
} from "@mui/material";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { selectUser } from "../../features/userSlice";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { LoadingState } from "../../components/UI/States";
import {
  PRIORITIES,
  REQUEST_TYPES,
  fetchMyFeatureRequests,
  submitFeatureRequest,
} from "./featureRequestsApi";
import { STATUS_META, statusLabel } from "./featureRequestStatus";

// Screenshots are stored on the request document as base64 data URLs (same as
// order problem photos), so compress client-side to the same per-image cap.
const MAX_IMAGES = 3;
const MAX_DATA_URL_LENGTH = 650000;

const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      let scale = Math.min(1, 1600 / Math.max(img.width, img.height));
      let quality = 0.8;
      for (let attempt = 0; attempt < 8; attempt++) {
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        if (dataUrl.length <= MAX_DATA_URL_LENGTH) return resolve(dataUrl);
        if (quality > 0.5) quality -= 0.15;
        else scale *= 0.7;
      }
      reject(new Error("screenshot could not be compressed small enough"));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("could not read that image"));
    };
    img.src = url;
  });

const SectionLabel = ({ children, hint, required }) => (
  <div>
    <p className="text-sm font-semibold">
      {children}
      {required && (
        <span className="ml-0.5 text-rose-500 dark:text-rose-400">*</span>
      )}
    </p>
    {hint && <p className="app-muted mt-0.5 text-xs">{hint}</p>}
  </div>
);

const Section = ({ children, className = "" }) => (
  <section className={`app-card rounded-2xl border p-5 ${className}`}>
    {children}
  </section>
);

// The requester's earlier asks, so they can see what's already in the queue
// instead of submitting the same thing twice.
const MyRequests = ({ requests }) => {
  if (!requests?.length) return null;
  return (
    <div className="app-card mt-6 overflow-hidden rounded-2xl border">
      <div className="app-divider border-b px-5 py-4">
        <h2 className="font-semibold">Your Requests</h2>
        <p className="app-muted mt-0.5 text-xs">
          Everything you've sent in, newest first.
        </p>
      </div>
      <ul className="divide-y divide-[color:var(--app-divider)]">
        {requests.map((request) => (
          <li
            key={request._id}
            className="flex items-center justify-between gap-3 px-5 py-3"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{request.title}</p>
              <p className="app-muted text-xs">
                {request.requestType} ·{" "}
                {request.createdAt
                  ? new Date(request.createdAt).toLocaleDateString()
                  : ""}
              </p>
            </div>
            <Chip
              label={statusLabel(request.status)}
              color={STATUS_META[request.status]?.chip || "default"}
              size="small"
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

const FeatureRequestForm = () => {
  const user = useSelector(selectUser);
  const [searchParams] = useSearchParams();

  const [title, setTitle] = useState("");
  const [requestType, setRequestType] = useState(REQUEST_TYPES[0]);
  const [priority, setPriority] = useState(PRIORITIES[0]);
  // Prefilled from the page the floating prompt was opened on.
  const [area, setArea] = useState(searchParams.get("page") || "");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]); // { name, dataUrl }
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [myRequests, setMyRequests] = useState([]);

  const loadMyRequests = () => {
    if (!user?.email) return;
    fetchMyFeatureRequests(user.email)
      .then(setMyRequests)
      .catch(() => setMyRequests([]));
  };

  useEffect(loadMyRequests, [user?.email]);

  const handleImageChange = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    setSubmitError(null);

    const room = MAX_IMAGES - images.length;
    if (picked.length > room) {
      setSubmitError(`You can attach up to ${MAX_IMAGES} screenshots.`);
    }
    for (const file of picked.slice(0, Math.max(0, room))) {
      try {
        const dataUrl = await compressImage(file);
        setImages((prev) =>
          prev.length >= MAX_IMAGES
            ? prev
            : [...prev, { name: file.name, dataUrl }]
        );
      } catch (err) {
        setSubmitError(`Couldn't add "${file.name}" — ${err.message}.`);
      }
    }
  };

  const removeImage = (index) =>
    setImages((prev) => prev.filter((_, i) => i !== index));

  const canSubmit = title.trim() && description.trim() && !submitting;

  const resetForm = () => {
    setTitle("");
    setRequestType(REQUEST_TYPES[0]);
    setPriority(PRIORITIES[0]);
    setDescription("");
    setImages([]);
    setSubmitError(null);
    setSubmitted(false);
  };

  const submitForm = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      await submitFeatureRequest({
        title: title.trim(),
        requestType,
        priority,
        area: area.trim(),
        pagePath: searchParams.get("page") || "",
        description: description.trim(),
        requesterName: user?.name || "",
        requesterEmail: user?.email || "",
        branch: user?.branch || "",
        source: searchParams.get("page") ? "web_prompt" : "web_form",
        images: images.map((image) => ({
          data: image.dataUrl,
          name: image.name,
          mimeType: "image/jpeg",
        })),
      });
      setSubmitted(true);
      loadMyRequests();
    } catch (err) {
      setSubmitError(err.message || "Could not submit your request.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user?.name) {
    return (
      <PageShell size="sm">
        <LoadingState label="Loading User Data..." className="py-24" />
      </PageShell>
    );
  }

  if (submitted) {
    return (
      <PageShell size="sm" className="sm:py-12">
        <div className="app-card flex flex-col items-center rounded-2xl border px-6 py-10 text-center sm:px-10">
          <span className="app-icon-tile h-14 w-14 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-300">
            <TaskAltIcon fontSize="large" />
          </span>
          <h1 className="mt-4 text-2xl font-bold tracking-tight">
            Request submitted!
          </h1>
          <p className="app-muted mt-2 max-w-md text-sm leading-relaxed">
            Thanks — your request is in the queue. You'll get an email when
            someone replies or the status changes.
          </p>
          <Button variant="contained" className="!mt-6" onClick={resetForm}>
            Submit Another
          </Button>
        </div>
        <MyRequests requests={myRequests} />
      </PageShell>
    );
  }

  return (
    <PageShell size="sm">
      <PageHeader
        icon={LightbulbOutlinedIcon}
        tone="amber"
        eyebrow="Forms"
        title="Request a Feature or Change"
        description="Tell us what would make Node work better for you."
      />

      <div className="space-y-5">
        {/* What kind of request */}
        <Section className="space-y-3">
          <SectionLabel hint="Pick the closest match.">
            What are you asking for?
          </SectionLabel>
          <div className="grid gap-2 sm:grid-cols-2">
            {REQUEST_TYPES.map((type) => {
              const selected = requestType === type;
              return (
                <button
                  key={type}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setRequestType(type)}
                  className={`flex min-h-[2.75rem] items-center gap-2 rounded-xl border p-3 text-left text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
                    selected
                      ? "border-sky-500 bg-sky-500/10 font-medium dark:border-sky-400"
                      : "app-surface hover:border-sky-400"
                  }`}
                >
                  {selected ? (
                    <CheckCircleIcon
                      className="shrink-0 text-sky-600 dark:text-sky-300"
                      sx={{ fontSize: 18 }}
                    />
                  ) : (
                    <RadioButtonUncheckedIcon
                      className="app-muted shrink-0"
                      sx={{ fontSize: 18 }}
                    />
                  )}
                  {type}
                </button>
              );
            })}
          </div>
        </Section>

        <Section className="space-y-5">
          {/* Title */}
          <div className="space-y-2">
            <SectionLabel
              required
              hint="One line — this is what shows up in the request list."
            >
              Short Summary
            </SectionLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Add a print button to the quote page"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              inputProps={{ maxLength: 200, "aria-label": "Short Summary" }}
            />
          </div>

          {/* Area + priority */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <SectionLabel hint="Which page or part of Node does this involve?">
                Page or Area
              </SectionLabel>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Web Leads, /order-problems"
                value={area}
                onChange={(e) => setArea(e.target.value)}
                inputProps={{ "aria-label": "Page or Area" }}
              />
            </div>
            <div className="space-y-2">
              <SectionLabel hint="How much is this holding you up?">
                Priority
              </SectionLabel>
              <TextField
                select
                fullWidth
                size="small"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                SelectProps={{
                  SelectDisplayProps: { "aria-label": "Priority" },
                }}
              >
                {PRIORITIES.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <SectionLabel
              required
              hint="What should it do, and what are you trying to accomplish? Details help a lot."
            >
              Details
            </SectionLabel>
            <TextField
              fullWidth
              multiline
              minRows={5}
              placeholder="Describe what you'd like added or changed..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              inputProps={{ "aria-label": "Details" }}
            />
          </div>
        </Section>

        {/* Screenshots */}
        <Section className="space-y-3">
          <SectionLabel
            hint={`Optional — up to ${MAX_IMAGES} screenshots make it much easier to see what you mean.`}
          >
            Screenshots
          </SectionLabel>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              component="label"
              variant="outlined"
              disabled={images.length >= MAX_IMAGES}
              startIcon={<AddPhotoAlternateIcon />}
            >
              Add Screenshots
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handleImageChange}
              />
            </Button>
            <span className="app-muted text-xs tabular-nums">
              {images.length} of {MAX_IMAGES}
            </span>
          </div>
          {images.length > 0 && (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {images.map((image, index) => (
                <div
                  key={`${image.name}-${index}`}
                  className="app-surface relative aspect-square overflow-hidden rounded-xl border"
                >
                  <img
                    src={image.dataUrl}
                    alt={image.name}
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute right-1.5 top-1.5 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
                    aria-label={`Remove ${image.name}`}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Section>

        {submitError && (
          <div
            role="alert"
            className="rounded-xl bg-rose-500/10 px-4 py-3 text-sm font-medium text-rose-700 dark:text-rose-300"
          >
            {submitError}
          </div>
        )}

        {/* Footer */}
        <div className="app-card flex flex-col gap-3 rounded-2xl border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <p className="app-muted text-sm">
              Submitting as <strong>{user.name}</strong>
            </p>
            {!canSubmit && !submitting && (
              <p className="app-muted text-xs">
                Add a short summary and details to submit.
              </p>
            )}
          </div>
          <Button
            variant="contained"
            size="large"
            disabled={!canSubmit}
            onClick={submitForm}
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {submitting ? "Submitting..." : "Submit Request"}
          </Button>
        </div>
      </div>

      <MyRequests requests={myRequests} />
    </PageShell>
  );
};

export default FeatureRequestForm;
